import { Filter, getLoanById, LoanStatusExternal } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { SWRConfig } from 'swr';

import { useFetchOrderList } from '@/components/modules/_shared/hooks/useFetchOrderList';
import { OrderDataGridFields } from '@/components/modules/OrderList/constants/OrderDataGridFields/OrderDataGridFields';
import { DetailedLoanStatusTag } from '@/components/shared/components/DetailedLoanStatusTag';
import { MultiFilteredList } from '@/components/shared/components/MultiFilteredList';
import { Text } from '@/components/shared/components/Text';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { ApplicationTypeMap } from '@/config/maps/ApplicationType.map';
import { ALLOWED_ORDER_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/config/orderConfig';
import { ApplicationType } from '@/config/types';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useCache } from '@/hooks/requests/useCache';
import { useCollectionScope } from '@/hooks/useCollectionScope';
import { forceRefreshDataGridTable } from '@/utils/forceRefreshDataGridTable';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

export const OrderListByApplicationType = ({
  applicationType,
  listProps,
}: {
  applicationType: ApplicationType;
  listProps?: {
    additionalFilters?: Filter[];
    reducedWidth?: boolean;
  };
}) => {
  const { fetchOrderList, fetchXLSXExport } = useFetchOrderList({
    applicationType: applicationType,
    additionalFilters: listProps?.additionalFilters,
  });
  const navigate = useNavigate();
  const scope = useCollectionScope();

  const {
    state: { lastViewedLoanId },
    actions: { updateOperationsContext },
  } = useOperationsContext();

  useEffect(() => {
    forceRefreshDataGridTable();
  }, [applicationType]);

  const loan = lastViewedLoanId ? useCache(getLoanById, { loanId: lastViewedLoanId })?.data : undefined;

  const ORDER_STATUSES_ITEMS = ALLOWED_ORDER_STATUSES_FOR_MULTI_SELECT_FILTER;

  const tableColumns = OrderDataGridFields({
    applicationType: applicationType,
    reducedWidth: listProps?.reducedWidth,
  });

  const liveResource = ApplicationTypeMap.toListResource(applicationType);

  const getDynamicPagePathByOnboardingFulfilledBy = () => {
    switch (applicationType) {
      case ApplicationType.ORDER:
        return DYNAMIC_PAGE_PATHS.ORDER;
      case ApplicationType.PAY_BY_LINK:
        return DYNAMIC_PAGE_PATHS.PAY_BY_LINK;
      default:
        return DYNAMIC_PAGE_PATHS.ORDER;
    }
  };

  return (
    <SWRConfig value={{ revalidateIfStale: false }}>
      <MultiFilteredList
        fetchList={fetchOrderList}
        getXLSXExport={fetchXLSXExport}
        dataGridFields={tableColumns}
        liveUpdates={liveResource ? { resource: liveResource, scope } : undefined}
        onRowClick={(selectedRow) => {
          updateOperationsContext({ lastViewedLoanId: selectedRow.row.loanId });
          navigate(generatePath(getDynamicPagePathByOnboardingFulfilledBy(), { loanId: selectedRow.row.loanId }));
        }}
        getRowClassName={(params) => {
          if (params.row.loanId === loan?.id) return 'last-viewed-application';
          else return '';
        }}
        multiselectProps={{
          field: 'loan_status_external',
          label: <Text tId={'LOAN_STATUS_EXTERNAL'} />,
          defaultLabel: <Text tId={'DEFAULT_STATUSES'} />,
          items: ORDER_STATUSES_ITEMS,
          checkBoxRender: (value) => {
            return <DetailedLoanStatusTag status={value} applicationType={ApplicationType.ORDER} />;
          },
          filtersValueHandler: (valueArray: string[]) => {
            if (valueArray?.includes(LoanStatusExternal.INITIAL)) {
              valueArray.push(LoanStatusExternal.ON_HOLD);
            }
            return valueArray;
          },
          defaultItems: [
            LoanStatusExternal.CONFIRMED,
            LoanStatusExternal.REQUESTED,
            LoanStatusExternal.CAPTURED,
            LoanStatusExternal.PAID,
            LoanStatusExternal.DENIED,
            LoanStatusExternal.INITIAL,
            LoanStatusExternal.APPROVED,
            LoanStatusExternal.CANCELLED,
            LoanStatusExternal.DESISTED,
          ],
        }}
        dateFilterProps={{
          dateField: 'updated_at',
        }}
        defaultOrderBy={'updated_at'}
        showExport
        disableSelectionOnClick
      />
    </SWRConfig>
  );
};

export const OrderListByApplicationTypeModule = withPermissionsModule(OrderListByApplicationType);
